import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import moment from "moment";
import {
  Breadcrumb,
  Input,
  Table,
  Space,
  Popconfirm,
  Tag,
  Tooltip,
} from "antd";
import { Row } from "antd";
import "../../../styles/Main.scss";
import SearchIcon from "../../../image/SearchIcon.svg";
import { getRequest, postRequest } from "../../../axios";
import { ReactComponent as ArchiveRestore } from "../../../image/ArchiveRestore.svg";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import { authUserData } from "../../../utils/Helper";

const { Search } = Input;

const extractArchivedGeneralVendorRfqList = (apiResponse) => {
  const payload = apiResponse?.data ?? apiResponse;
  const resultsWrapper = payload?.results;

  const rawList = Array.isArray(resultsWrapper?.data)
    ? resultsWrapper.data
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(resultsWrapper)
        ? resultsWrapper
        : [];

  return {
    rawList,
    count: Number(payload?.count) || rawList.length,
  };
};

const formatDate = (raw) => {
  if (!raw) return "—";
  const m = moment(raw);
  return m.isValid() ? m.format("DD-MM-YY") : String(raw);
};

const formatDateTime = (raw) => {
  if (!raw) return "—";
  const m = moment(raw);
  return m.isValid() ? m.format("DD-MM-YY HH:mm") : String(raw);
};

const formatAmount = (raw, currency) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return "—";
  const symbol = currency === "INR" ? "₹" : currency ? `${currency} ` : "";
  return `${symbol}${n.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })}`;
};

const renderRfqType = (value) => {
  const key = String(value ?? "").toLowerCase();
  if (key === "quick") return "Quick RFQ";
  if (key === "general") return "General RFQ";
  return value ?? "—";
};

const renderRfqStatus = (status) => {
  if (!status) return "—";
  const s = String(status).toLowerCase();
  let color = "default";
  if (s.includes("draft")) color = "default";
  else if (s.includes("under approval") || s.includes("pending"))
    color = "processing";
  else if (s.includes("floated") || s.includes("sent") || s.includes("open"))
    color = "processing";
  else if (s.includes("approve")) color = "success";
  else if (s.includes("reject") || s.includes("cancel")) color = "error";
  else if (s.includes("offer") || s.includes("negotiation")) color = "warning";
  return <Tag color={color}>{String(status)}</Tag>;
};

const ArchiveGeneralRFQHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [archiveList, setArchiveList] = useState([]);
  const VendorId = authUserData()?.vendor ? authUserData()?.vendor : null;
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [reload, setReload] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
    search: "",
  });

  const fetchArchivedList = useCallback(async () => {
    if (!VendorId) return;

    setLoading(true);
    try {
      const params = {
        page: state.page,
        pagesize: state.pagesize,
        ordering: state.ordering,
        vendor_id: VendorId,
      };
      if (state.search?.trim()) {
        params.search = state.search.trim();
      }

      const apiResponse = await getRequest("general-rfq-archive/", params);
      const { rawList, count } = extractArchivedGeneralVendorRfqList(apiResponse);
      setArchiveList(rawList);
      setTotalCount(count);
    } catch (error) {
      ErrorNotificationMsg(t("error_in_archive_rfq_history"));
      setArchiveList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [VendorId, state, t]);

  useEffect(() => {
    fetchArchivedList();
  }, [fetchArchivedList, reload]);

  const handleTableChange = (pagination, filters, sorter) => {
    const orderBy = getSortingEnumValue(sorter.columnKey, sorter.order);
    setState((prev) => ({
      ...prev,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    }));
  };

  const getSortingEnumValue = (sortField, sortOrder) => {
    if (sortField && sortOrder === "ascend") return sortField;
    if (sortField && sortOrder === "descend") return `-${sortField}`;
    return "id";
  };

  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };

  const onSearch = (value) => {
    setState((prev) => ({
      ...prev,
      page: 1,
      search: value,
    }));
  };

  const debounceLoadData = debounce((value, searchHandler) => {
    searchHandler(value);
  }, 1000);

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRow(selectedRowKeys);
    },
  };

  const handleRestore = useCallback(
    async (id = "") => {
      const ids = selectedRow.length > 0 ? [...selectedRow] : [];
      if (ids.length === 0 && id !== "" && id != null) {
        ids.push(id);
      }
      if (ids.length === 0) return;

      try {
        setRestoring(true);
        const apiResponse = await postRequest("general-rfq-restore/", {
          deleted: ids.map((itemId) => Number(itemId) || itemId),
        });
        const data = apiResponse?.data;
        const succeeded =
          data?.success === true ||
          data?.success === "true" ||
          (data?.success !== false && (apiResponse?.status ?? 200) < 400);

        if (succeeded) {
          SuccessNotificationMsg(
            (typeof data?.message === "string" && data.message) ||
              t("rfq_history_restore_successfully")
          );
          setSelectedRow([]);
          setReload((prev) => !prev);
        } else {
          ErrorNotificationMsg(
            (typeof data?.message === "string" && data.message) ||
              t("error_in_restore_rfq_history")
          );
        }
      } catch (error) {
        ErrorNotificationMsg(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            t("error_in_restore_rfq_history")
        );
      } finally {
        setRestoring(false);
      }
    },
    [selectedRow, t]
  );

  const itemColumns = useMemo(
    () => [
      {
        title: "Sr.",
        key: "sr",
        width: 60,
        render: (_, __, idx) => idx + 1,
      },
      {
        title: "Item Description",
        dataIndex: "item_description",
        key: "item_description",
        render: (v) => v ?? "—",
      },
      {
        title: "Approved Make",
        dataIndex: "approved_make",
        key: "approved_make",
        width: 150,
        render: (v) => v || "—",
      },
      {
        title: "Qty.",
        dataIndex: "quantity",
        key: "quantity",
        width: 100,
        align: "right",
        render: (v) => {
          const n = Number(v);
          return Number.isFinite(n)
            ? n.toLocaleString("en-IN", { maximumFractionDigits: 3 })
            : v ?? "—";
        },
      },
      {
        title: "UoM",
        dataIndex: "unit_of_measurement",
        key: "unit_of_measurement",
        width: 100,
        render: (v) => v ?? "—",
      },
      {
        title: "Unit Rate",
        dataIndex: "unit_rate",
        key: "unit_rate",
        width: 120,
        align: "right",
        render: (v) => {
          const n = Number(v);
          return Number.isFinite(n)
            ? n.toLocaleString("en-IN", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })
            : v ?? "—";
        },
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: 130,
        align: "right",
        render: (v, record) => {
          const n = Number(v);
          const currency = record?.__parentCurrency;
          if (!Number.isFinite(n)) return v ?? "—";
          const symbol =
            currency === "INR" ? "₹" : currency ? `${currency} ` : "";
          return `${symbol}${n.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}`;
        },
      },
      {
        title: "Remarks from Buyer",
        dataIndex: "remarks_from_buyer",
        key: "remarks_from_buyer",
        render: (v) => v || "—",
      },
    ],
    []
  );

  const renderExpandedItems = (record) => {
    const items = Array.isArray(record?.items) ? record.items : [];
    if (!items.length) {
      return (
        <div style={{ padding: "8px 12px", color: "#6B7280" }}>
          No items in this RFQ.
        </div>
      );
    }

    const dataWithCurrency = items.map((item) => ({
      ...item,
      __parentCurrency: record?.currency,
    }));

    return (
      <Table
        size="small"
        columns={itemColumns}
        dataSource={dataWithCurrency}
        pagination={false}
        rowKey={(item, idx) => item?.id ?? `${record?.id ?? "row"}-${idx}`}
        scroll={{ x: 900 }}
        style={{ margin: "8px 0" }}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        title: "Client Name",
        dataIndex: "company_name",
        key: "company_name",
        width: 180,
        sorter: true,
        render: (v) => v ?? "—",
      },
      {
        title: "RFQ #",
        dataIndex: "id",
        key: "id",
        width: 90,
        sorter: true,
        render: (v) => (v != null ? `RFQ-${v}` : "—"),
      },
      {
        title: "RFQ Type",
        dataIndex: "rfq_type",
        key: "rfq_type",
        width: 120,
        render: renderRfqType,
      },
      {
        title: "Created By",
        dataIndex: "created_by_name",
        key: "created_by_name",
        width: 140,
        sorter: true,
        render: (v) => v ?? "—",
      },
      {
        title: "Bid Due Date & Time",
        dataIndex: "rfq_bid_due_date_time",
        key: "rfq_bid_due_date_time",
        width: 170,
        sorter: true,
        render: (v) => formatDateTime(v),
      },
      {
        title: "Delivery Date",
        dataIndex: "delivery_date",
        key: "delivery_date",
        width: 130,
        sorter: true,
        render: (v) => formatDate(v),
      },
      {
        title: "Items",
        dataIndex: "items",
        key: "items",
        width: 80,
        align: "center",
        render: (v) => (Array.isArray(v) ? v.length : 0),
      },
      {
        title: "Total Amount",
        dataIndex: "total_amount",
        key: "total_amount",
        width: 140,
        align: "right",
        sorter: true,
        render: (v, record) => formatAmount(v, record?.currency),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 180,
        sorter: true,
        render: (v) => renderRfqStatus(v),
      },
      {
        title: "Action",
        key: "operation",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Space>
            <Popconfirm
              className="action"
              title={t("pop_restore_title")}
              okText={t("pop_ok")}
              cancelText={t("pop_cancel")}
              onConfirm={() => handleRestore(record.id)}
            >
              <Tooltip title="Restore" placement="left" color="#094E8F">
                <ArchiveRestore className="ArchiverestorImg" />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleRestore, t]
  );

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>Archived General RFQ History - Vendor</h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/rfq-vendor-general-request-for-quotation/history/1">
                  General RFQ History - Vendor
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                {t("Archived RFQ History - Vendor")}
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 250,
                  }}
                />
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label={t("pl_restore")}
                  loading={restoring}
                  disabled={restoring}
                />
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <Row className="tblheader" />
          <div className="tablewrp">
            <Table
              rowSelection={rowSelection}
              columns={columns}
              rowKey={(record) => record.id}
              dataSource={archiveList}
              loading={loading}
              expandable={{
                expandedRowRender: renderExpandedItems,
                rowExpandable: (record) =>
                  Array.isArray(record?.items) && record.items.length > 0,
              }}
              pagination={{
                current: state.page,
                pageSize: state.pagesize,
                total: totalCount,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} items`,
                locale: {
                  items_per_page: "Items / Page",
                },
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
            />
          </div>
          <div className="FormFooter Container">
            <ButtonField
              className="CancelGradiantBtn"
              type="primary"
              htmlType="button"
              label={t("previous")}
              onClick={() =>
                navigate("/rfq-vendor-general-request-for-quotation/history/1")
              }
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default ArchiveGeneralRFQHistory;
